import {
  useHistoryAssetsByIdQuery,
  useHistoryAssetsQuery,
  useHistoryOperationModesByIdQuery
} from '@domain/assets'
import {useScheduleQuery} from '@domain/schedule'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Box, CircularProgress, Collapse, Skeleton, Stack} from '@mui/material'
import {
  AddEditAssetCommentDialog,
  AssetLabel,
  CommentsSidebar,
  useAssetCommentsQuery
} from '@widgets/planning-comments'
import {
  PlanningChartGantt,
  PlanningChartLabels,
  PlanningChartLegend,
  ScheduleItemsContainer,
  TimeScaleFn,
  getLocalizedDayMonthTimeFormat,
  toPixel,
  usePlanningChartStartEnd
} from '@widgets/planning-schedule-chart'
import moment, {Moment} from 'moment-timezone'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {CollapsibleCard} from '../components/CollapsibleCard'
import {OperatorChartCardActions} from '../components/OperatorChartCardActions'
import {FEATURE_NAMESPACE, OperatorViewLSKeys} from '../consts'
import {useLocalStorageSettings} from '../hooks'

interface OperatorChartCardProps {
  plantCode: string
  startOfPlan: Moment
  endOfPlan: Moment
  dateLabelHeight: number
  hourlyPriceHeight: number
  labelColumnWidth: number
  cellHeight: number
  cellWidth: number
  hoursDisplayed: number
  xScale: TimeScaleFn
  hoursList: Moment[]
  shouldShowExactPrices?: boolean
  shouldShowActualPricesIndicator?: boolean
  shouldShowColorCodedElectricityPrices?: boolean
  shouldShowScheduleItemComments?: boolean
  shouldShowAssetComments?: boolean
}

const _OperatorChartCard = React.forwardRef<HTMLDivElement, OperatorChartCardProps>(
  (
    {
      plantCode,
      startOfPlan,
      endOfPlan,
      dateLabelHeight,
      hourlyPriceHeight,
      labelColumnWidth,
      cellHeight,
      cellWidth,
      hoursDisplayed,
      xScale,
      hoursList,
      shouldShowExactPrices = false,
      shouldShowActualPricesIndicator = false,
      shouldShowColorCodedElectricityPrices = false,
      shouldShowScheduleItemComments = false,
      shouldShowAssetComments = false
    },
    ref
  ) => {
    const timezoneId = useUpmPlantTimezone()
    const {
      t,
      i18n: {language}
    } = useTranslation(FEATURE_NAMESPACE)
    const {startOfChart, endOfChart} = usePlanningChartStartEnd()
    const commentSidebarWidth = Math.max(cellWidth * hoursDisplayed * 0.2, 400)

    const {settings: cardSettings, updateSettings: setCardSettings} = useLocalStorageSettings(
      OperatorViewLSKeys.ChartCardSettings,
      {isCardOpen: true}
    )
    const isCardOpen = cardSettings.isCardOpen
    const toggleCardOpen = useCallback(
      () => setCardSettings({isCardOpen: !isCardOpen}),
      [setCardSettings, isCardOpen]
    )

    const {settings: sidebarSettings, updateSettings: setSidebarSettings} = useLocalStorageSettings(
      OperatorViewLSKeys.CommentsSidebarSettings,
      {
        isCommentSidebarOpen: false
      }
    )
    const isCommentSidebarOpen = sidebarSettings.isCommentSidebarOpen
    const toggleCommentsSidebar = useCallback(() => {
      setSidebarSettings({isCommentSidebarOpen: !isCommentSidebarOpen})
    }, [setSidebarSettings, isCommentSidebarOpen])

    const closeCommentsSidebar = useCallback(
      () => setSidebarSettings({isCommentSidebarOpen: false}),
      [setSidebarSettings]
    )

    const {data: schedule} = useScheduleQuery({plantId: plantCode, range: [startOfPlan, endOfPlan]})

    const {data: sortedAssets} = useHistoryAssetsQuery({
      plantId: plantCode,
      timeFrame: [startOfPlan, endOfPlan],
      sorted: true
    })

    const scheduleLastUpdatedAt = schedule?.lastSaved.savedAt
    const scheduleLastUpdatedBy = schedule?.lastSaved.savedBy

    const shouldShowPriceInfo = shouldShowExactPrices || shouldShowColorCodedElectricityPrices
    const isCommentSidebarEnabled = shouldShowScheduleItemComments || shouldShowAssetComments

    const assetIds = sortedAssets?.map((asset) => asset.id)
    const {data: assetComments} = useAssetCommentsQuery(assetIds, !shouldShowAssetComments)

    const {isPending: isPendingOperations} = useHistoryOperationModesByIdQuery({
      plantId: plantCode,
      timeFrame: [startOfPlan, endOfPlan]
    })
    const {isPending: isPendingAssets} = useHistoryAssetsByIdQuery({
      plantId: plantCode,
      timeFrame: [startOfPlan, endOfPlan]
    })

    return (
      <CollapsibleCard
        isCollapsed={!isCardOpen}
        onToggleCollapsed={toggleCardOpen}
        title={t('planning.planningSectionTitle')}
        subtitle={
          scheduleLastUpdatedAt &&
          scheduleLastUpdatedBy &&
          t('planning.lastSaved', {
            date: getLocalizedDayMonthTimeFormat(
              moment.utc(scheduleLastUpdatedAt),
              timezoneId,
              language
            ),
            name: scheduleLastUpdatedBy
          })
        }
        headerContent={
          <Stack direction="row" sx={{justifyContent: 'flex-end'}}>
            <OperatorChartCardActions
              toggleCommentsSidebar={toggleCommentsSidebar}
              isCardCollapsed={!isCardOpen}
              schedule={schedule}
              assets={sortedAssets}
              startOfChart={startOfChart}
              endOfChart={endOfChart}
            />
          </Stack>
        }
        {...dataTestId('operator_chart_card')}
      >
        <Collapse in={isCardOpen} sx={{position: 'relative'}}>
          {isCommentSidebarEnabled && schedule && sortedAssets && (
            <CommentsSidebar
              width={commentSidebarWidth}
              isOpen={isCommentSidebarOpen}
              closeSidebar={closeCommentsSidebar}
              isReadOnly={true}
              schedule={schedule}
              assets={sortedAssets}
              startOfPlan={startOfPlan}
              endOfPlan={endOfPlan}
              startOfChart={startOfChart}
              endOfChart={endOfChart}
            />
          )}
          <Stack
            sx={{overflow: 'hidden'}}
            spacing={2}
            {...dataTestId('operator_chart_card_content')}
          >
            {shouldShowColorCodedElectricityPrices && (
              <PlanningChartLegend
                shouldShowActualPricesLegend={shouldShowActualPricesIndicator}
                startOfPlan={startOfPlan}
                endOfPlan={endOfPlan}
              />
            )}
            <AddEditAssetCommentDialog
              startOfPlan={startOfPlan}
              endOfPlan={endOfPlan}
              areCommentsEnabled={shouldShowAssetComments}
            />
            <Stack direction="row">
              <Box sx={{mt: toPixel(2 * dateLabelHeight)}}>
                {sortedAssets ? (
                  <PlanningChartLabels
                    sortedAssets={sortedAssets}
                    hourlyPriceHeight={hourlyPriceHeight}
                    cellHeight={cellHeight}
                    width={labelColumnWidth}
                    shouldShowPriceInfo={shouldShowPriceInfo}
                    renderAssetLabel={(asset, labelWidth) => (
                      <AssetLabel
                        asset={asset}
                        comment={assetComments?.find(
                          ({commentsCategoryInstanceId}) =>
                            commentsCategoryInstanceId === asset.id.toString()
                        )}
                        width={labelWidth}
                        isReadOnly={true}
                        shouldShowComment={shouldShowAssetComments}
                      />
                    )}
                  />
                ) : (
                  <Stack sx={{pt: toPixel(hourlyPriceHeight)}}>
                    {Array.from({length: 10}).map((_, index) => (
                      <Skeleton
                        key={index}
                        variant="text"
                        width={labelColumnWidth}
                        height={cellHeight}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
              {isPendingOperations || isPendingAssets ? (
                <CircularProgress />
              ) : (
                <PlanningChartGantt
                  ref={ref}
                  startOfPlan={startOfPlan}
                  endOfPlan={endOfPlan}
                  numberOfAssets={sortedAssets?.length}
                  dateLabelHeight={dateLabelHeight}
                  hourlyPriceHeight={hourlyPriceHeight}
                  cellHeight={cellHeight}
                  cellWidth={cellWidth}
                  hoursDisplayed={hoursDisplayed}
                  xScale={xScale}
                  hoursList={hoursList}
                  shouldShowExactPrices={shouldShowExactPrices}
                  shouldShowColorCodedElectricityPrices={shouldShowColorCodedElectricityPrices}
                  shouldShowActualPricesIndicator={shouldShowActualPricesIndicator}
                  shouldShowCurrentTime={true}
                  shouldRoundCurrentTime={false}
                  renderScheduleItems={(params) => (
                    <ScheduleItemsContainer
                      {...params}
                      shouldShowFixedForOptimizerInfo={false}
                      shouldShowColorCodedElectricityPrices={shouldShowColorCodedElectricityPrices}
                      shouldShowExactElectricityPrices={shouldShowExactPrices ?? false}
                      shouldShowStartEndTime
                      shouldShowScheduleItemComments={shouldShowScheduleItemComments}
                    />
                  )}
                />
              )}
            </Stack>
          </Stack>
        </Collapse>
      </CollapsibleCard>
    )
  }
)

export const OperatorChartCard = React.memo(_OperatorChartCard)
OperatorChartCard.displayName = 'OperatorChartCard'
